import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsPage = () => {
  const [pageVisible, setPageVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Terms of Service — Insust Global';
    const t = setTimeout(() => setPageVisible(true), 50);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className={`min-h-screen bg-slate-950 transition-opacity duration-700 ${pageVisible ? 'opacity-100' : 'opacity-0'}`}>
      <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 lg:px-12 h-16 bg-slate-950/90 backdrop-blur-lg border-b border-slate-800/50">
        <Link to="/" className="flex items-center gap-3">
          <img src="/logo.jpeg" alt="Insust Global logo" className="h-8 w-8 rounded-full object-contain" />
          <span className="text-lg font-bold text-white">Insust Global</span>
        </Link>
        <Link to="/" className="flex items-center gap-2 text-sm text-slate-400 hover:text-teal-400 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>
      </nav>

      <div className="pt-28 pb-20 px-6 lg:px-12">
        <div className="max-w-[780px] mx-auto">
          <span className="text-xs font-mono tracking-[0.2em] uppercase text-teal-400 block mb-4">Legal</span>
          <h1 className="text-4xl font-bold text-white mb-2">Terms of Service</h1>
          <p className="text-sm text-slate-500 mb-12">Last updated: March 2026</p>

          <div className="prose-sm space-y-8 text-slate-400 leading-relaxed">
            <section>
              <h2 className="text-lg font-bold text-white mb-3">1. Acceptance of Terms</h2>
              <p>By accessing or using the Insust Global website, platform, products, or services ("Services"), you agree to be bound by these Terms of Service. If you do not agree, do not use our Services.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">2. Services</h2>
              <p>Insust Global provides AI-powered geospatial intelligence platforms, spatial analytics solutions, GIS training courses, and related professional services. Our Services are designed for enterprise, institutional, and individual use.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">3. Accounts & Registration</h2>
              <p>Some Services may require registration. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to provide accurate and current information.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">4. Course Enrollment</h2>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Course enrollments are subject to availability and confirmation by our team</li>
                <li>Tuition fees are as listed at the time of enrollment; discount codes are valid until their stated expiry</li>
                <li>Refund requests must be submitted at least 7 days before the cohort start date</li>
                <li>Course materials are for personal use and may not be redistributed</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">5. Intellectual Property</h2>
              <p>All content, software, data, maps, designs, and materials on our platform are the property of Insust Global or its licensors. You may not copy, modify, distribute, or create derivative works without prior written consent.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">6. Acceptable Use</h2>
              <p>You agree not to:</p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Use our Services for any unlawful purpose</li>
                <li>Attempt to gain unauthorized access to our systems or data</li>
                <li>Interfere with or disrupt the integrity of our platform</li>
                <li>Reverse-engineer, decompile, or disassemble any part of our software</li>
                <li>Scrape, collect, or harvest data from our platform without authorization</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">7. Data & Privacy</h2>
              <p>Your use of our Services is also governed by our <Link to="/privacy" className="text-teal-400 hover:underline">Privacy Policy</Link>. By using our Services, you consent to the collection and use of information as described in that policy.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">8. Limitation of Liability</h2>
              <p>To the maximum extent permitted by law, Insust Global shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our Services. Our total liability shall not exceed the amount paid by you for the specific Service giving rise to the claim.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">9. Disclaimer</h2>
              <p>Our Services are provided "as is" and "as available" without warranties of any kind, either express or implied. We do not guarantee that our platform will be uninterrupted, error-free, or free of harmful components.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">10. Changes to Terms</h2>
              <p>We reserve the right to modify these Terms at any time. Material changes will be communicated via the website or email. Continued use of our Services after changes constitutes acceptance of the updated Terms.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">11. Governing Law</h2>
              <p>These Terms shall be governed by and construed in accordance with the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in Kolkata, West Bengal.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">12. Contact</h2>
              <p>For questions regarding these Terms, contact us at:<br />
                <a href="mailto:legal@insust.in" className="text-teal-400 hover:underline">legal@insust.in</a>
              </p>
              <p className="mt-2">Insust Global</p>
            </section>
          </div>
        </div>
      </div>

      <footer className="bg-slate-950 border-t border-slate-800 py-6 px-6 lg:px-16 flex flex-col sm:flex-row items-center justify-between gap-4">
        <span className="text-xs text-slate-600">&copy; {new Date().getFullYear()} Insust Global. All rights reserved.</span>
        <div className="flex gap-4 text-xs text-slate-600">
          <Link to="/privacy" className="hover:text-teal-400 transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="text-teal-400/60">Terms of Service</Link>
          <Link to="/" className="hover:text-teal-400 transition-colors">Home</Link>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
